import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import dayjs from "dayjs";

// Types
import {
  mapAvailableTriggerB2F,
  mapBonusB2F,
  mapBonusFreeSpins,
  mapBonusNominalB2F,
  mapFreeSpinB2F,
  mapTriggersFreeSpins,
} from "mappings/bonuses";
import { ChangeStringValues } from "types/Auth";
import { BalanceResponse } from "types/Balance";
import {
  BonusFreeSpinsMappedType,
  BonusListType,
  BonusMappedType,
  BonusNominalMappedType,
  BonusTriggerMappedType,
  TriggerFreeSpinMappedType,
} from "types/Bonus";
import { PhoneCountry } from "types/PhoneCountries";
import {
  AvailableTriggerCompaingFreeSpinsType,
  AvailableTriggerCompaingType,
} from "types/TriggerCompaings";
import {
  BonusFreeSpinResponseDataType,
  BonusNominalResponseDataType,
  BonusResponseDataType,
  FreeSpinResponseDataType,
  UserType,
} from "types/User";

export type UserState = {
  id: number | null;
  balance: number;
  bonus: number;
  bonuses: BonusMappedType[];
  bonusNominal: Array<BonusNominalMappedType | BonusFreeSpinsMappedType>;
  triggerCompaings: Array<BonusTriggerMappedType | TriggerFreeSpinMappedType>;
  freeSpins: BonusMappedType[];
  canceledBonus: BonusListType | null;
  currency: string;
  name: string;
  isBanned: boolean | null;
  firstDeposit: boolean | null;
  emailRecovery: string;
  login: string;
  birthday: string;
  country: string;
  registered: string;
  sumBet: number;
  sumDeposit: number;
  countDeposit: number;
  welcomeExtraBonusExpire: string;
  referrerBalance: number;
  phoneNumber: string;
  phoneCountry: Nullable<PhoneCountry>;
};

const initialState: UserState = {
  id: null,
  balance: 0,
  bonus: 0,
  bonuses: [],
  bonusNominal: [],
  triggerCompaings: [],
  freeSpins: [],
  canceledBonus: null,
  currency: "",
  name: "",
  login: "",
  isBanned: null,
  firstDeposit: null,
  emailRecovery: "",
  birthday: "",
  country: "",
  registered: "",
  sumBet: 0,
  sumDeposit: 0,
  countDeposit: 0,
  welcomeExtraBonusExpire: "",
  referrerBalance: 0,
  phoneNumber: "",
  phoneCountry: null,
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    changeStringField(
      state,
      action: PayloadAction<
        ChangeStringValues | { field: string; value: PhoneCountry }
      >,
    ) {
      const { field, value } = action.payload;
      state[field] = value;
    },
    setCurrency(state, action: PayloadAction<string>) {
      state.currency = action.payload;
    },
    setUser(state, action: PayloadAction<UserType>) {
      const {
        id,
        name,
        balance,
        banned,
        birthday,
        firstDeposit,
        login,
        geo,
        currencyCode,
        createdAt,
        phoneNumber,
      } = action.payload;
      state.id = id;
      state.balance = balance.balance;
      state.bonus = balance.bonusBalance;
      state.referrerBalance = balance.referrerBalance;
      state.login = login;
      if (name) {
        state.name = name;
      }
      if (birthday) {
        state.birthday = dayjs(birthday).format("DD-MM-YYYY");
      }
      state.currency = currencyCode;
      state.country = geo;
      state.isBanned = banned;
      state.firstDeposit = firstDeposit;
      state.registered = createdAt;
      state.sumBet = balance.info.sumCommissionBet;
      state.sumDeposit = balance.info.sumCommissionDeposit;
      state.countDeposit = balance.info.countDeposit;
      state.welcomeExtraBonusExpire = dayjs(createdAt).add(1, "hours").format();
      state.phoneNumber = phoneNumber;
    },
    setBalanceUpdate(state, action: PayloadAction<BalanceResponse>) {
      const { balance, bonusBalance, referrerBalance } = action.payload;
      // eslint-disable-next-line no-console
      console.log("setBalanceUpdate", action.payload);
      state.balance = balance;
      state.bonus = bonusBalance;
      state.referrerBalance = referrerBalance;
    },
    setEmail(state, action: PayloadAction<string>) {
      state.login = action.payload;
    },
    setEmailRecovery(state, action: PayloadAction<string>) {
      state.emailRecovery = action.payload;
    },
    setBonuses(state, action: PayloadAction<BonusResponseDataType[]>) {
      const mappedBonuses = action.payload.map(mapBonusB2F);
      state.bonuses = mappedBonuses;
    },
    setBonusNominal(
      state,
      action: PayloadAction<
        Array<BonusNominalResponseDataType | BonusFreeSpinResponseDataType>
      >,
    ) {
      const mappedBonuses = action.payload.map((bonus) =>
        "freeSpinsCount" in bonus
          ? mapBonusFreeSpins(bonus)
          : mapBonusNominalB2F(bonus),
      );
      state.bonusNominal = mappedBonuses;
    },
    setTriggerCompaings(
      state,
      action: PayloadAction<
        Array<
          AvailableTriggerCompaingType | AvailableTriggerCompaingFreeSpinsType
        >
      >,
    ) {
      const mappedTriggerCompaings = action.payload.map((bonus) =>
        "freeSpinsCount" in bonus
          ? mapTriggersFreeSpins(bonus)
          : mapAvailableTriggerB2F(bonus),
      );
      state.triggerCompaings = mappedTriggerCompaings;
    },
    setFreeSpins(state, action: PayloadAction<FreeSpinResponseDataType[]>) {
      const mappedFreeSpins = [...action.payload]
        .sort((a, b) => {
          const aFrom = dayjs(a.validFrom).get("milliseconds");
          const bFrom = dayjs(b.validFrom).get("milliseconds");
          return aFrom - bFrom;
        })
        .map(mapFreeSpinB2F);
      state.freeSpins = mappedFreeSpins;
    },
    setCanceledBonus(state, action: PayloadAction<BonusListType>) {
      state.canceledBonus = action.payload;
    },
    deleteBonus(state) {
      if (state.bonuses) {
        state.bonuses = state.bonuses.filter(
          (bonus) =>
            bonus.id !==
            (state.canceledBonus && "id" in state.canceledBonus
              ? state.canceledBonus.id
              : null),
        );
      }
    },
    deleteFeeSpin(state) {
      if (state.freeSpins) {
        state.freeSpins = state.freeSpins.filter(
          (bonus) =>
            bonus.id !==
            (state.canceledBonus && "id" in state.canceledBonus
              ? state.canceledBonus.id
              : null),
        );
      }
    },
  },
});

export const {
  changeStringField,
  setBonuses,
  setBonusNominal,
  setTriggerCompaings,
  setBalanceUpdate,
  setCurrency,
  setFreeSpins,
  setUser,
  setEmail,
  setEmailRecovery,
  setCanceledBonus,
  deleteBonus,
  deleteFeeSpin,
} = userSlice.actions;

export default userSlice.reducer;
